import React, { useState, useEffect } from "react";

import Column from "./Column";
import Icon from "./Icon";
import PropTypes from "prop-types";
import Row from "./Row";
import { Text } from "./Texts";
import _find from "lodash/find";
import colors from "../styles/colors";
import styled from "styled-components";
import { Search } from "styled-icons/bootstrap";
import { useTranslation } from "react-i18next";
import decodeHTMLEntities from "modules/utils/DecodeHTMLEntities";

const Dropdown = ({
  items = [],
  onSelected,
  minWidth,
  position,
  CustomDropdownButton,
  defaultIndex = 0,
  disabled,
  noBorder = false,
  selectedIndex = false,
  searchType,
}) => {
  const [isOptionsVisible, setOptionsVisible] = useState();
  const [selectedItem, setSelectedItem] = useState();
  let selectedTitle;
  const [searchInput, setSearchInput] = useState("");
  const [filteredItems, setFilteredItems] = useState(items);
  const [t] = useTranslation();

  let allHyphen = items.every((obj) => obj.title === "-");

  useEffect(() => {
    if (searchType && !allHyphen) {
      setFilteredItems(items.filter((item) => item.title.toLowerCase().includes(searchInput.toLowerCase())));
    }
  }, [items, searchInput, allHyphen, searchType]);

  if (selectedItem && _find(items, { title: selectedItem.title })) {
    selectedTitle = selectedItem.title;
  }

  if (!selectedTitle && items && items[defaultIndex]) {
    selectedTitle = items[defaultIndex].title;
  } else if (!selectedTitle) {
    selectedTitle = "-";
  } else if (selectedIndex && selectedTitle != items[selectedIndex].title) {
    selectedTitle = items[selectedIndex].title;
  }

  if (typeof selectedTitle !== "object") {
    selectedTitle = <Text size="12px">{decodeHTMLEntities(selectedTitle)}</Text>;
  }

  const onDropdownButtonClicked = () => {
    if (!disabled) {
      setOptionsVisible(!isOptionsVisible);
    }
  };
  const onDropdownItemSelected = (item, index) => {
    onDropdownButtonClicked();
    setSelectedItem(item);
    onSelected(item, index);
  };

  if (typeof items === "object") {
    items = Object.values(items);
  }

  const viewItems = items.map((item, index) => (
    <DropdownItem key={index} item={item} index={index} onClick={onDropdownItemSelected}></DropdownItem>
  ));

  return (
    <Container>
      {CustomDropdownButton ? (
        <CustomDropdownButton onClick={onDropdownButtonClicked}></CustomDropdownButton>
      ) : (
        <DropdownButton
          className={`${noBorder === true ? "no-border" : ""} dropdown-button`}
          alignItems="center"
          columnSpacing="8px"
          minWidth={minWidth}
          onClick={onDropdownButtonClicked}
          disabled={disabled}
        >
          <Column width="100%">{selectedTitle}</Column>
          <Column wrap>
            <Icon name="icon-down-dir" size="14px"></Icon>
          </Column>
        </DropdownButton>
      )}
      <ItemsContainer isVisible={isOptionsVisible} position={position} minWidth={minWidth}>
        {searchType && !allHyphen ? (
          <>
            <SearchContent>
              <SearchIcon color="#555" />
              <StyledInput type="text" className="input-box" onChange={(e) => setSearchInput(e.target.value)} />
            </SearchContent>
            {filteredItems.length > 0 ? (
              <ScrollElement>
                {
                  filteredItems.map((item, index) => (
                <DropdownItem key={index} item={item} index={index} onClick={onDropdownItemSelected}></DropdownItem>
              ))
                }
              </ScrollElement>
            ) : (
              <StyledItem>
                <div className="account-not-found">{t("__fbads_ads_account_not_found")}</div>
              </StyledItem>
            )}
          </>
        ) : (
          <ScrollElement>
            {viewItems}
          </ScrollElement>
        )}
      </ItemsContainer>
      {isOptionsVisible && <Backdrop onClick={() => setOptionsVisible(false)} />}
    </Container>
  );
};

Dropdown.propTypes = {
  item: PropTypes.array,
  onSelected: PropTypes.func,
  minWidth: PropTypes.string,
  position: PropTypes.object,
  CustomDropdownButton: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  defaultIndex: PropTypes.number,
};

export default Dropdown;

const DropdownItem = ({ item, index, onClick }) => {
  return (
    <StyledItem onClick={() => onClick(item, index)}>
      {typeof item.title === "object" ? (
        decodeHTMLEntities(item.title)
      ) : (
        <Text size="12px">{decodeHTMLEntities(item.title)}</Text>
      )}
    </StyledItem>
  );
};

const Container = styled.div`
  position: relative;

  > *:first-child {
    cursor: pointer;
  }

  .custom-dropdown-button {
    display: flex;
    align-items: center;

    > * {
      min-width: 0;
    }
  }
`;

const DropdownButton = styled(Row).attrs((props) => ({
  minWidth: props.minWidth,
  opacity: props.disabled ? 0.75 : 1,
}))`
  border: 1px solid #f0f0f0;
  padding: 4px 8px 4px 16px;
  border-radius: 8px;
  box-sizing: border-box;
  min-width: ${(props) => props.minWidth};
  opacity: ${(props) => props.opacity};
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")} !important;
  background-color: #fff;

  &.no-border {
    border: none;
    background-color: transparent;
  }

  &.no-border .column > p {
    font-size: 1.2rem;
  }
`;

const ItemsContainer = styled.div.attrs((props) => ({
  display: props.isVisible ? "block" : "none",
  top: props.position ? props.position.top : "35px",
  right: props.position && props.position.right ? props.position.right : null,
  minWidth: props.minWidth,
}))`
  display: ${(props) => props.display};
  position: absolute;
  border: 1px solid #f0f0f0;
  background-color: ${colors.backgroundSection};
  box-shadow: ${colors.sectionShadow} 0 10px 30px;
  min-width: 150px;
  border-radius: 8px;
  z-index: 999;
  top: ${(props) => props.top};
  right: ${(props) => props.right};
  min-width: ${(props) => props.minWidth};
  padding: 16px 0;
  text-align: left;
  max-width: 100%;

  @media screen and (max-width: 834px) {
    box-shadow: none;
    width: calc(100% - 32px);
    position: fixed;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    margin: 16px;
    text-align: center;
    max-height: calc(100vh - 150px);
    min-width: unset;

    p {
      font-size: 14px;
    }
  }
`;

const ScrollElement = styled.div`
  max-height: 170px;
  overflow: auto;
`;

const StyledItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;

  .page-not-found {
    font-size: 12px;
    color: #555;
    line-height: 1rem;
  }

  @media screen and (max-width: 834px) {
    padding: 16px;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 998;

  @media screen and (max-width: 834px) {
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const SearchIcon = styled(Search).attrs((props) => ({
  color: props.color,
}))`
  color: ${(props) => props.color};
  width: 16px;
  height: 16px;
  margin-right: 5px;
  position: absolute;
  left: 16px;
  top: 24px;
`;

const SearchContent = styled.div`
  display: flex;
  flex-direction: row;
  line-height: 1.5rem;
  font-size: 12px;
`;

const StyledInput = styled.input`
  margin: 2px 10px;
  padding: 6px 6px 6px 30px;
  border: 1px solid #ddd;
  border-radius: 8px;
  width 100%;

  &:only-child {
    margin-bottom: 0;
  }
`;
